import type * as ClassTypes from './types/class';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import type {IValidation} from '@/components/form/validation/types';
import Observer from './observer';
import {Utils} from './utils';
import Validation from '@/components/form/validation';

const validation: IValidation = new Validation();
const observer: IObserver = new Observer();

// Поведение шапки
export const initHeader = (headerWrap: HTMLElement): void => {
    import(/* webpackChunkName: "header" */ '@/components/header')
        .then(({default: Header}: IDynamicImport) => {
            const header: ClassTypes.IHeader = new Header();

            header.init({headerWrap});
        });
};

// Инициализация попапов
export const initPopup = (buttons: HTMLElement[], otherOptions: Partial<ClassTypes.IPopupFacadeOptions> = {}): void => {
    import(/* webpackChunkName: "popup-facade" */ '@/components/popup/popupFacade')
        .then(({default: PopupFacade}: IDynamicImport) => {
            const options: ClassTypes.IPopupFacadeOptions = {
                ...{openButtons: buttons},
                ...otherOptions
            };

            const t: ClassTypes.IPopupFacade = new PopupFacade(options);

            t.init();
        });
};

// Инициализация форм
export const initForm = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "form" */ '@/components/form')
        .then(({default: Form}: IDynamicImport) => {
            new Form().init({
                target        : wrapper,
                successMessage: true
            });
        });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = (): void => {
    observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
        if (!popup.content) {
            return;
        }

        // Форма
        const callbackFormElement = popup.content.querySelector('.j-form') as HTMLElement;

        if (callbackFormElement) {
            validation.update();
            import(/* webpackChunkName: "form" */ '@/components/form')
                .then(({default: Form}: IDynamicImport) => {
                    new Form().init({
                        target        : callbackFormElement,
                        popupInstance : popup,
                        successMessage: true
                    });
                });
        }

        // FileUploader
        const fileInput = popup.content.querySelector('.j-input-file-wrapper') as HTMLElement;

        import(/* webpackChunkName: "FileUploader" */ '@/components/form/file')
            .then(({default: FileUploader}: IDynamicImport) => {
                new FileUploader().init(fileInput);
            });

        // Cadastral
        const cadastralItem = popup.content.querySelector('.j-cadastral') as HTMLElement;

        import(/* webpackChunkName: "Cadastral" */ '@/components/form/cadastral-number')
            .then(({default: Cadastral}: IDynamicImport) => {
                new Cadastral().init(cadastralItem);
            });
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons: HTMLElement[]): void => {
    const options = {
        removeDelay         : 300,
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify              : 'popup_theme_callback'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

/* Подключение ленивой загрузки (img/picture/iframe) */
export const lazyLoadElements = (lazyElements: Element[]): void => {
    const replaceSource = (element: Element): void => {
        const parentElement = element.parentElement;

        if (!parentElement) {
            return;
        }

        if (parentElement.tagName === 'PICTURE' && element.tagName === 'IMG') {
            element.addEventListener('load', () => {
                parentElement.style.backgroundImage = '';
            });
        }

        const targetAttribute = element.nodeName === 'SOURCE' ? 'srcset' : 'src';
        const sourceElement = element.nodeName === 'SOURCE' ? 'data-srcset' : 'data-src';

        if (element.getAttribute(sourceElement) !== null) {
            element.setAttribute(targetAttribute, element.getAttribute(sourceElement) || '');
            element.removeAttribute(sourceElement);
        }
    };

    if ('IntersectionObserver' in window) {
        // Иначе по событию IntersectionObserver
        const options = {
            rootMargin: '0px 0px 50% 0px'
        };

        const lazyImageObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;

                    replaceSource(lazyEl);
                    lazyImageObserver.unobserve(lazyEl);
                }
            });
        }, options);

        lazyElements.forEach((lazyImage: Element) => {
            if (Utils.isInViewport(lazyImage as HTMLElement,
                window.innerHeight || document.documentElement.clientHeight)) {
                replaceSource(lazyImage);
            } else {
                lazyImageObserver.observe(lazyImage);
            }
        });
    } else {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyElements.forEach((lazyEl: Element) => {
            replaceSource(lazyEl);
        });
    }
};

// Инициализация первого экрана
export const initFirstScreen = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "firstScreen" */ '@/sections/first-screen')
        .then(({default: FirstScreen}: IDynamicImport) => {
            new FirstScreen().init({wrapper});
        });
};

// Инициализация слайдера
// eslint-disable-next-line
export const initSliderSection = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "sliderSection" */ '@/sections/slider')
        .then(({default: SliderSection}: IDynamicImport) => {
            new SliderSection().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Табы
export const initTabs = (elem: HTMLElement): void => {
    import(/* webpackChunkName: "tabs" */ '@/components/tabs')
        .then(({default: Tabs}: IDynamicImport) => {
            new Tabs().init({elem});
        });
};

// Видео
export const initVideo = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "video" */ '@/components/video')
        .then(({default: Video}: IDynamicImport) => {
            new Video().init(wrapper);
        });
};

// Подключение туглеров
export const initTogglers = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "toggle" */ '@/components/toggle')
        .then(({default: Toggle}: IDynamicImport) => {
            new Toggle().init({wrapper});
        });
};

// Подключение сервисов
export const initServices = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "services" */ '@/sections/services')
        .then(({default: Services}: IDynamicImport) => {
            new Services().init(wrapper);
        });
};

// Подключение аккардионов
export const initDetails = (items: HTMLDetailsElement[]): void => {
    import(/* webpackChunkName: "details" */ '@/components/details')
        .then(({default: Details}: IDynamicImport) => {
            new Details().init(items);
        });
};

// Вакансии
export const initVacancies = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "vacancies" */ '@/sections/vacancies')
        .then(({default: Vacancies}: IDynamicImport) => {
            new Vacancies().init(wrapper);
        });
};

// Вопросы
export const initQuestions = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "questions" */ '@/sections/questions')
        .then(({default: Questions}: IDynamicImport) => {
            new Questions().init(wrapper);
        });
};

// Тендеры
export const initTenders = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "tenders" */ '@/sections/tenders')
        .then(({default: Tenders}: IDynamicImport) => {
            new Tenders().init(wrapper);
        });
};

// Подключение FileUploader
export const initFileUploader = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "FileUploader" */ '@/components/form/file')
        .then(({default: FileUploader}: IDynamicImport) => {
            new FileUploader().init(wrapper);
        });
};

// Подключение Cadastral
export const initCadastral = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "Cadastral" */ '@/components/form/cadastral-number')
        .then(({default: Cadastral}: IDynamicImport) => {
            new Cadastral().init(wrapper);
        });
};
